import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { ExpandMore as ExpandMoreIcon, Psychology as PsychologyIcon, Close as CloseIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import apiAxiosInstance from '../../utils/axiosInstance';
import aiAxiosInstance from '../../utils/aiAxiosInstance';

interface Question {
  _id: string;
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation: string;
  standardReference: string;
  bloomsLevel: string;
}

interface Passage {
  _id: string;
  title: string;
  passage: string;
  questions: Question[];
  passage2Questions?: Question[];
  combinedQuestions?: Question[];
  isPairedPassage?: boolean;
  passage2Title?: string;
}

interface Test {
  _id: string;
  title: string;
  passages: Passage[];
}

interface StudentSubmission {
  _id: string;
  studentInfo: {
    firstName: string;
    lastName: string;
  };
  answers: Record<string, string>;
  statusOverrides?: Record<string, 'correct' | 'incorrect'>;
  score: number;
  status: 'submitted' | 'graded';
  submittedAt: string;
  groupId?: { _id: string; name: string } | string | null;
}

interface StudentMisconceptionAnalysis {
  studentName: string;
  totalQuestions: number;
  incorrectAnswers: number;
  overallScore: number;
  misconceptions: Array<{
    question: string;
    studentAnswer: string;
    correctAnswer: string;
    misconceptionType: string;
    explanation: string;
    cognitiveError: string;
    interventionSuggestion: string;
  }>;
  recommendations: string[];
  summary: string;
}

interface QuestionAnalysis {
  questionId: string;
  question: string;
  correctAnswer: string;
  totalStudents: number;
  correctCount: number;
  accuracy: number;
  standardReference: string;
  bloomsLevel: string;
  commonWrongAnswers: Array<{
    answer: string;
    count: number;
    percentage: number;
  }>;
  misconceptions: string[];
}

interface ClassAnalysis {
  testTitle: string;
  totalStudents: number;
  averageScore: number;
  questionsAnalyzed: number;
  overallSummary: string;
  questionAnalysis: QuestionAnalysis[];
  standardsAnalysis: Array<{
    standard: string;
    averageAccuracy: number;
    questionsCount: number;
    performance: string;
    analysis: string;
  }>;
  recommendations: string[];
  strengthAreas: string[];
  strugglingAreas: string[];
}

// Helper function to get the starting question number for a passage
const getStartingQuestionNumber = (passages: Passage[], currentPassageIndex: number): number => {
  let totalQuestions = 0;
  for (let i = 0; i < currentPassageIndex; i++) {
    const passage = passages[i];
    totalQuestions += passage.questions.length;
    if (passage.isPairedPassage) {
      totalQuestions += (passage.passage2Questions?.length || 0);
      totalQuestions += (passage.combinedQuestions?.length || 0);
    }
  }
  return totalQuestions;
};

const QuestionOverview: React.FC<{
  passages: Passage[];
  selectedQuestionId: string | null;
  onQuestionSelect: (questionId: string) => void;
}> = ({ passages, selectedQuestionId, onQuestionSelect }) => {
  return (
    <Box 
      sx={{ 
        width: '300px',
        height: '100vh',
        position: 'fixed',
        right: 0,
        top: 0,
        pt: 8,
        backgroundColor: '#fff',
        borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
        overflowY: 'auto'
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Questions Overview
        </Typography>
        {passages.map((passage, passageIndex) => (
          <Box key={passage._id} sx={{ mb: 3 }}>
            {/* Title for the passage(s) */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              {passage.isPairedPassage ? (
                <>Paired Passage {passageIndex + 1}: {passage.title} & {passage.passage2Title}</>
              ) : (
                <>Passage {passageIndex + 1}: {passage.title}</>
              )}
            </Typography>

            {/* First passage questions */}
            {passage.questions.map((question, questionIndex) => (
              <Accordion
                key={question._id}
                expanded={selectedQuestionId === question._id}
                onChange={() => onQuestionSelect(question._id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Typography>
                    Q{getStartingQuestionNumber(passages, passageIndex) + questionIndex + 1}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph sx={{ fontWeight: 'medium' }}>{question.question}</Typography>
                  {question.answers.map((answer, index) => (
                    <Typography 
                      key={index} 
                      sx={{ 
                        ml: 2, 
                        mb: 1,
                        display: 'flex',
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box component="span" sx={{ minWidth: '24px', fontWeight: 'medium' }}>
                        {String.fromCharCode(65 + index)}.
                      </Box>
                      <Box component="span">
                        {answer.replace(/^[A-Z]\.\s*/, '')}
                      </Box>
                    </Typography>
                  ))}
                  <Typography sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                    Correct: {question.correctAnswer}
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                    Standard: {question.standardReference}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Second passage questions */}
            {passage.isPairedPassage && passage.passage2Questions && passage.passage2Questions.map((question, questionIndex) => (
              <Accordion
                key={question._id}
                expanded={selectedQuestionId === question._id}
                onChange={() => onQuestionSelect(question._id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Typography>
                    Q{getStartingQuestionNumber(passages, passageIndex) + passage.questions.length + questionIndex + 1}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph sx={{ fontWeight: 'medium' }}>{question.question}</Typography>
                  {question.answers.map((answer, index) => (
                    <Typography 
                      key={index} 
                      sx={{ 
                        ml: 2, 
                        mb: 1,
                        display: 'flex',
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box component="span" sx={{ minWidth: '24px', fontWeight: 'medium' }}>
                        {String.fromCharCode(65 + index)}.
                      </Box>
                      <Box component="span">
                        {answer.replace(/^[A-Z]\.\s*/, '')}
                      </Box>
                    </Typography>
                  ))}
                  <Typography sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                    Correct: {question.correctAnswer}
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                    Standard: {question.standardReference}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Combined questions */}
            {passage.isPairedPassage && passage.combinedQuestions && passage.combinedQuestions.map((question, questionIndex) => (
              <Accordion
                key={question._id}
                expanded={selectedQuestionId === question._id}
                onChange={() => onQuestionSelect(question._id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Typography>
                    Q{getStartingQuestionNumber(passages, passageIndex) + passage.questions.length + (passage.passage2Questions?.length || 0) + questionIndex + 1}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph sx={{ fontWeight: 'medium' }}>{question.question}</Typography>
                  {question.answers.map((answer, index) => (
                    <Typography 
                      key={index} 
                      sx={{ 
                        ml: 2, 
                        mb: 1,
                        display: 'flex',
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box component="span" sx={{ minWidth: '24px', fontWeight: 'medium' }}>
                        {String.fromCharCode(65 + index)}.
                      </Box>
                      <Box component="span">
                        {answer.replace(/^[A-Z]\.\s*/, '')}
                      </Box>
                    </Typography>
                  ))}
                  <Typography sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                    Correct: {question.correctAnswer}
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                    Standard: {question.standardReference}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const GradeSubmissions: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [submissions, setSubmissions] = useState<StudentSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [resettingSubmissions, setResettingSubmissions] = useState<Set<string>>(new Set());
  const [reEnablingSubmissions, setReEnablingSubmissions] = useState<Set<string>>(new Set());
  
  // Filters
  const [groups, setGroups] = useState<Array<{ _id: string; name: string }>>([]);
  const [groupFilter, setGroupFilter] = useState<string>('all');
  
  // AI Analysis state
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<StudentMisconceptionAnalysis | null>(null);
  const [analyzingSubmissions, setAnalyzingSubmissions] = useState<Set<string>>(new Set());
  const [submissionsWithAnalysis, setSubmissionsWithAnalysis] = useState<Set<string>>(new Set());
  const [isCheckingAnalyses, setIsCheckingAnalyses] = useState(false);
  
  // Class analysis state
  const [classAnalysis, setClassAnalysis] = useState<ClassAnalysis | null>(null);
  const [classAnalysisDialogOpen, setClassAnalysisDialogOpen] = useState(false);
  const [analyzingClass, setAnalyzingClass] = useState(false);
  const [hasExistingClassAnalysis, setHasExistingClassAnalysis] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });
  
  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  const reEnableSubmission = async (submission: StudentSubmission) => {
    const submissionId = submission._id;
    setReEnablingSubmissions(prev => new Set([...prev, submissionId]));
    
    try {
      await apiAxiosInstance.delete(`/api/tests/${testId}/submissions/re-enable`, {
        params: { 
          firstName: submission.studentInfo.firstName, 
          lastName: submission.studentInfo.lastName 
        }
      });
      
      // Remove the submission from the list
      setSubmissions(prev => prev.filter(s => s._id !== submissionId));
      
      setError(`Test re-enabled for ${submission.studentInfo.firstName} ${submission.studentInfo.lastName}. Student can continue with their existing progress.`);
      setTimeout(() => setError(null), 5000);
      
    } catch (err: any) {
      console.error('Error re-enabling submission:', err);
      setError('Error re-enabling submission. Please try again.');
    } finally {
      setReEnablingSubmissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };

  const resetSubmission = async (submission: StudentSubmission) => {
    const submissionId = submission._id;
    setResettingSubmissions(prev => new Set([...prev, submissionId]));
    
    try {
      await apiAxiosInstance.delete(`/api/tests/${testId}/submissions/reset`, {
        params: { 
          firstName: submission.studentInfo.firstName, 
          lastName: submission.studentInfo.lastName 
        }
      });
      
      // Remove the submission from the list
      setSubmissions(prev => prev.filter(s => s._id !== submissionId));
      
      setError(`Test reset for ${submission.studentInfo.firstName} ${submission.studentInfo.lastName}. Student will start completely fresh.`);
      setTimeout(() => setError(null), 5000);
      
    } catch (err: any) {
      console.error('Error resetting submission:', err);
      setError('Error resetting submission. Please try again.');
    } finally {
      setResettingSubmissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };

  const analyzeWithAI = async (submission: StudentSubmission, isReAnalysis = false) => {
    if (!test) return;
    
    const submissionId = submission._id;
    setAnalyzingSubmissions(prev => new Set([...prev, submissionId]));
    
    try {
      // Collect all questions from all passages
      const allQuestions: any[] = [];
      test.passages.forEach(passage => {
        // Add regular questions
        allQuestions.push(...passage.questions);
        // Add paired passage questions if they exist
        if (passage.passage2Questions) {
          allQuestions.push(...passage.passage2Questions);
        }
        // Add combined questions if they exist
        if (passage.combinedQuestions) {
          allQuestions.push(...passage.combinedQuestions);
        }
      });

      // Prepare data for AI analysis
      const analysisData = {
        testId: testId!,
        studentName: `${submission.studentInfo.firstName} ${submission.studentInfo.lastName}`,
        testQuestions: allQuestions,
        studentAnswers: submission.answers,
        testPassages: test.passages.map(passage => ({
          title: passage.title,
          passage: passage.passage
        }))
      };

      const response = await aiAxiosInstance.post('/analyze-student-misconceptions', analysisData);
      const analysisResult = response.data;
      
      // Save the analysis to the database
      const saveData = {
        testId: testId!,
        submissionId: submissionId,
        studentName: `${submission.studentInfo.firstName} ${submission.studentInfo.lastName}`,
        totalQuestions: analysisResult.totalQuestions,
        incorrectAnswers: analysisResult.incorrectAnswers,
        overallScore: analysisResult.overallScore,
        misconceptions: analysisResult.misconceptions,
        recommendations: analysisResult.recommendations,
        summary: analysisResult.summary
      };

      await apiAxiosInstance.post('/api/ai-analysis', saveData);
      
      // Update the UI state to show this submission now has an analysis
      setSubmissionsWithAnalysis(prev => new Set([...prev, submissionId]));
      
      setCurrentAnalysis(analysisResult);
      setAnalysisDialogOpen(true);
      
      setSnackbar({
        open: true,
        message: isReAnalysis 
          ? `Re-analysis completed and saved for ${submission.studentInfo.firstName} ${submission.studentInfo.lastName}`
          : `AI analysis completed and saved for ${submission.studentInfo.firstName} ${submission.studentInfo.lastName}`,
        severity: 'success'
      });
      
    } catch (err: any) {
      console.error('Error analyzing with AI:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Error analyzing submission with AI. Please try again.',
        severity: 'error'
      });
    } finally {
      setAnalyzingSubmissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };

  const checkExistingAnalyses = async (submissions: StudentSubmission[], abortSignal?: AbortSignal) => {
    // Prevent duplicate analysis checks
    if (isCheckingAnalyses) {
      console.log('Analysis check already in progress, skipping...');
      return;
    }

    setIsCheckingAnalyses(true);
    
    try {
      const analysisChecks = await Promise.all(
        submissions.map(submission => 
          apiAxiosInstance.get(`/api/ai-analysis/submission/${submission._id}/exists`, {
            signal: abortSignal
          })
        )
      );
      
      const submissionsWithAnalysisSet = new Set<string>();
      analysisChecks.forEach((response, index) => {
        if (response.data.exists) {
          submissionsWithAnalysisSet.add(submissions[index]._id);
        }
      });
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setSubmissionsWithAnalysis(submissionsWithAnalysisSet);
      }
    } catch (error: any) {
      // Handle aborted/canceled requests (not real errors)
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.message === 'canceled') {
        console.log('Analysis check was aborted/canceled - this is normal in StrictMode');
        return;
      }
      console.error('Error checking existing analyses:', error);
    } finally {
      if (isMountedRef.current) {
        setIsCheckingAnalyses(false);
      }
    }
  };

  const viewExistingAnalysis = async (submission: StudentSubmission) => {
    if (!test) return;
    
    try {
      const response = await apiAxiosInstance.get(`/api/ai-analysis/${testId}/${submission._id}`);
      setCurrentAnalysis(response.data);
      setAnalysisDialogOpen(true);
    } catch (err: any) {
      console.error('Error loading existing analysis:', err);
      setSnackbar({
        open: true,
        message: 'Error loading existing analysis. Please try again.',
        severity: 'error'
      });
    }
  };

  const loadExistingClassAnalysis = async () => {
    try {
      const response = await apiAxiosInstance.get(`/api/class-analysis/${testId}`);
      return response.data;
    } catch (error) {
      // No existing analysis found
      return null;
    }
  };

  const analyzeClassPerformance = async (forceRegenerate = false) => {
    if (!test || submissions.length === 0) return;
    
    setAnalyzingClass(true);
    
    try {
      // Check if there's an existing analysis (unless forcing regeneration)
      if (!forceRegenerate) {
        const existingAnalysis = await loadExistingClassAnalysis();
        if (existingAnalysis) {
          setClassAnalysis(existingAnalysis);
          setClassAnalysisDialogOpen(true);
          setHasExistingClassAnalysis(true);
          setSnackbar({
            open: true,
            message: 'Loaded existing class analysis successfully!',
            severity: 'info'
          });
          setAnalyzingClass(false);
          return;
        }
      }
      // Collect all questions from all passages
      const allQuestions: any[] = [];
      test.passages.forEach(passage => {
        allQuestions.push(...passage.questions);
        if (passage.passage2Questions) {
          allQuestions.push(...passage.passage2Questions);
        }
        if (passage.combinedQuestions) {
          allQuestions.push(...passage.combinedQuestions);
        }
      });

      // Prepare data for AI class analysis
      const classData = {
        testId: testId!,
        testTitle: test.title,
        testQuestions: allQuestions,
        studentSubmissions: submissions.map(submission => ({
          studentName: `${submission.studentInfo.firstName} ${submission.studentInfo.lastName}`,
          answers: submission.answers,
          score: submission.score
        })),
        testPassages: test.passages.map(passage => ({
          title: passage.title,
          passage: passage.passage
        }))
      };

      const response = await aiAxiosInstance.post('/analyze-class-performance', classData);
      const classAnalysisResult = response.data;
      
      // Save the class analysis to the database
      try {
        // Clean and validate the data before saving
        const cleanQuestionAnalysis = classAnalysisResult.questionAnalysis.map((q: any) => ({
          questionId: q.questionId || '',
          question: q.question || '',
          correctAnswer: q.correctAnswer || '',
          totalStudents: q.totalStudents || 0,
          correctCount: q.correctCount || 0,
          accuracy: q.accuracy || 0,
          standardReference: q.standardReference || '',
          bloomsLevel: q.bloomsLevel || '',
          commonWrongAnswers: (q.commonWrongAnswers || []).map((wa: any) => ({
            answer: wa.answer || '',
            count: wa.count || 0,
            percentage: wa.percentage || 0
          })),
          misconceptions: q.misconceptions || []
        }));

        const cleanStandardsAnalysis = (classAnalysisResult.standardsAnalysis || []).map((s: any) => ({
          standard: s.standard || '',
          questionsCount: s.questionsCount || 0,
          averageAccuracy: s.averageAccuracy || 0,
          performance: s.performance || 'Needs Support',
          analysis: s.analysis || ''
        }));

        const saveData = {
          testId: testId!,
          testTitle: classAnalysisResult.testTitle || '',
          totalStudents: classAnalysisResult.totalStudents || 0,
          averageScore: classAnalysisResult.averageScore || 0,
          questionsAnalyzed: classAnalysisResult.questionsAnalyzed || 0,
          overallSummary: classAnalysisResult.overallSummary || '',
          standardsAnalysis: cleanStandardsAnalysis,
          questionAnalysis: cleanQuestionAnalysis,
          recommendations: classAnalysisResult.recommendations || [],
          strengthAreas: classAnalysisResult.strengthAreas || [],
          strugglingAreas: classAnalysisResult.strugglingAreas || []
        };

        await apiAxiosInstance.post('/api/class-analysis', saveData);
        console.log('Class analysis saved successfully to database');
      } catch (saveError) {
        console.error('Error saving class analysis to database:', saveError);
        // Don't block the UI - still show the analysis even if save fails
      }
      
      setClassAnalysis(classAnalysisResult);
      setHasExistingClassAnalysis(true);
      setClassAnalysisDialogOpen(true);
      
      setSnackbar({
        open: true,
        message: 'Class performance analysis completed and saved successfully!',
        severity: 'success'
      });
      
    } catch (err: any) {
      console.error('Error analyzing class performance:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Error analyzing class performance. Please try again.',
        severity: 'error'
      });
    } finally {
      setAnalyzingClass(false);
    }
  };

  const fetchSubmissions = async (abortController: AbortController, groupId?: string) => {
    const params: any = {};
    if (groupId && groupId !== 'all') params.groupId = groupId;
    const submissionsResponse = await apiAxiosInstance.get(`/api/tests/${testId}/submissions`, {
      signal: abortController.signal,
      params
    });
    return submissionsResponse.data;
  };

  useEffect(() => {
    isMountedRef.current = true; // Reset mounted state for this effect
    const abortController = new AbortController();
    
    const fetchData = async () => {
      try {
        // Fetch test details
        const testResponse = await apiAxiosInstance.get(`/api/tests/${testId}`, {
          signal: abortController.signal
        });
        const processedTest = {
          ...testResponse.data,
          passages: testResponse.data.passages.map((passage: Passage, index: number) => {
            if (passage.isPairedPassage && testResponse.data.passages[index + 1]) {
              return {
                ...passage,
                passage2Title: testResponse.data.passages[index + 1].title
              };
            }
            return passage;
          })
        };
        if (isMountedRef.current) setTest(processedTest);

        // Prefetch groups (for filter dropdown)
        const groupsRes = await apiAxiosInstance.get('/api/groups', { signal: abortController.signal });
        const groupsList = (groupsRes.data || []).map((g: any) => ({ _id: g._id, name: g.name }));
        if (isMountedRef.current) setGroups(groupsList);

        // Fetch submissions with optional group filter
        const fetchedSubmissions = await fetchSubmissions(abortController, groupFilter);
        if (isMountedRef.current) setSubmissions(fetchedSubmissions);

        // Check which submissions have existing analyses
        await checkExistingAnalyses(fetchedSubmissions, abortController.signal);

        // Check if there's an existing class analysis
        const existingClassAnalysis = await loadExistingClassAnalysis();
        if (existingClassAnalysis) setHasExistingClassAnalysis(true);
      } catch (err: any) {
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED' || err.message === 'canceled') {
          return;
        }
        if (isMountedRef.current) setError('Error loading test submissions. Please try again.');
        console.error('Error fetching test data:', err);
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    if (testId) fetchData();
    return () => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, [testId]);

  // Refetch when groupFilter changes
  useEffect(() => {
    const abortController = new AbortController();
    const run = async () => {
      try {
        const data = await fetchSubmissions(abortController, groupFilter);
        if (isMountedRef.current) setSubmissions(data);
      } catch {}
    };
    if (testId) run();
    return () => abortController.abort();
  }, [groupFilter, testId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!test) {
    return (
      <Box p={3}>
        <Alert severity="error">Test not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box sx={{ flexGrow: 1, maxWidth: 'calc(100% - 300px)', p: 3, pb: 8 }}>
        <Typography variant="h4" gutterBottom>
          {test.title} - Submissions
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {submissions.length} submissions • {test.passages.reduce((sum, p) => sum + p.questions.length, 0)} questions
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="group-filter-label">Filter by Group</InputLabel>
              <Select
                labelId="group-filter-label"
                label="Filter by Group"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
              >
                <MenuItem value="all">All Groups</MenuItem>
                {groups.map(g => (
                  <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {submissions.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<PsychologyIcon />}
                onClick={() => analyzeClassPerformance()}
                disabled={analyzingClass}
              >
                {analyzingClass ? 'Analyzing Class...' : 
                 hasExistingClassAnalysis ? 'View Class Analysis' : 'Analyze Class Performance'}
              </Button>
            )}
          </Box>
        </Box>

        {submissions.length === 0 ? (
          <Alert severity="info">No submissions yet.</Alert>
        ) : (
          <Box>
            {submissions.map((submission) => {
              const groupName = typeof submission.groupId === 'object' && submission.groupId !== null
                ? (submission.groupId as any).name
                : undefined;
              return (
                <Accordion key={submission._id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={3}>
                        <Typography>
                          {submission.studentInfo.firstName} {submission.studentInfo.lastName}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography color="text.secondary">
                          {new Date(submission.submittedAt).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography>
                          Score: {submission.score}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        {groupName && (
                          <Chip label={groupName} size="small" />
                        )}
                      </Grid>
                      <Grid item xs={3}>
                        <Chip
                          label={submission.status}
                          color={submission.status === 'graded' ? 'success' : 'warning'}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails>
                    {test.passages.map((passage, passageIndex) => (
                      <Box key={passage._id} sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          {passage.isPairedPassage ? (
                            <>Paired Passage {Math.floor(passageIndex/2) + 1}: {passage.title} & {test.passages[passageIndex + 1]?.title || ''}</>
                          ) : (
                            <>Passage {passageIndex + 1}: {passage.title}</>
                          )}
                        </Typography>
                        
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell width="35%">Question</TableCell>
                                <TableCell width="15%">Student's Answer</TableCell>
                                <TableCell width="15%">Correct Answer</TableCell>
                                <TableCell width="20%">Standard</TableCell>
                                <TableCell width="15%">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {/* First Passage Questions */}
                              {passage.questions.map((question, questionIndex) => {
                                const questionNumber = getStartingQuestionNumber(test.passages, passageIndex) + questionIndex + 1;
                                return (
                                  <TableRow 
                                    key={question._id}
                                    sx={{
                                      backgroundColor: selectedQuestionId === question._id ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                      }
                                    }}
                                    onClick={() => setSelectedQuestionId(question._id)}
                                  >
                                    <TableCell>
                                      {questionNumber}. {question.question}
                                    </TableCell>
                                    <TableCell>{submission.answers[question._id]}</TableCell>
                                    <TableCell>{question.correctAnswer}</TableCell>
                                    <TableCell>{question.standardReference}</TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                          label={(() => {
                                            const override = submission.statusOverrides?.[question._id];
                                            if (override === 'correct') return 'Correct';
                                            if (override === 'incorrect') return 'Incorrect';
                                            return submission.answers[question._id] === question.correctAnswer ? 'Correct' : 'Incorrect';
                                          })()}
                                          color={(() => {
                                            const override = submission.statusOverrides?.[question._id];
                                            if (override === 'correct') return 'success';
                                            if (override === 'incorrect') return 'error';
                                            return submission.answers[question._id] === question.correctAnswer ? 'success' : 'error';
                                          })() as any}
                                          size="small"
                                        />
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          clickable
                                          icon={<SwapHorizIcon fontSize="small" />}
                                          label="Toggle"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const current = submission.statusOverrides?.[question._id];
                                            const newStatus = current === 'correct' ? 'incorrect' : 'correct';
                                            try {
                                              const res = await apiAxiosInstance.patch(`/api/tests/${testId}/submissions/${submission._id}/answers/${question._id}/status`, { status: newStatus });
                                              const { submission: updated } = res.data;
                                              setSubmissions(prev => prev.map(s => s._id === submission._id ? {
                                                ...s,
                                                statusOverrides: updated.statusOverrides,
                                                score: updated.score
                                              } : s));
                                              setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
                                            } catch (err: any) {
                                              console.error('Error updating status', err);
                                              setSnackbar({ open: true, message: err.response?.data?.message || 'Error updating status', severity: 'error' });
                                            }
                                          }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Second Passage Questions */}
                              {passage.isPairedPassage && passage.passage2Questions && passage.passage2Questions.map((question, questionIndex) => {
                                const questionNumber = getStartingQuestionNumber(test.passages, passageIndex) + passage.questions.length + questionIndex + 1;
                                return (
                                  <TableRow 
                                    key={question._id}
                                    sx={{
                                      backgroundColor: selectedQuestionId === question._id ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                      }
                                    }}
                                    onClick={() => setSelectedQuestionId(question._id)}
                                  >
                                    <TableCell>
                                      {questionNumber}. {question.question}
                                    </TableCell>
                                    <TableCell>{submission.answers[question._id]}</TableCell>
                                    <TableCell>{question.correctAnswer}</TableCell>
                                    <TableCell>{question.standardReference}</TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                          label={(() => {
                                            const override = submission.statusOverrides?.[question._id];
                                            if (override === 'correct') return 'Correct';
                                            if (override === 'incorrect') return 'Incorrect';
                                            return submission.answers[question._id] === question.correctAnswer ? 'Correct' : 'Incorrect';
                                          })()}
                                          color={(() => {
                                            const override = submission.statusOverrides?.[question._id];
                                            if (override === 'correct') return 'success';
                                            if (override === 'incorrect') return 'error';
                                            return submission.answers[question._id] === question.correctAnswer ? 'success' : 'error';
                                          })() as any}
                                          size="small"
                                        />
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          clickable
                                          icon={<SwapHorizIcon fontSize="small" />}
                                          label="Toggle"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const current = submission.statusOverrides?.[question._id];
                                            const newStatus = current === 'correct' ? 'incorrect' : 'correct';
                                            try {
                                              const res = await apiAxiosInstance.patch(`/api/tests/${testId}/submissions/${submission._id}/answers/${question._id}/status`, { status: newStatus });
                                              const { submission: updated } = res.data;
                                              setSubmissions(prev => prev.map(s => s._id === submission._id ? {
                                                ...s,
                                                statusOverrides: updated.statusOverrides,
                                                score: updated.score
                                              } : s));
                                              setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
                                            } catch (err: any) {
                                              console.error('Error updating status', err);
                                              setSnackbar({ open: true, message: err.response?.data?.message || 'Error updating status', severity: 'error' });
                                            }
                                          }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Combined Questions */}
                              {passage.isPairedPassage && passage.combinedQuestions && passage.combinedQuestions.map((question, questionIndex) => {
                                const questionNumber = getStartingQuestionNumber(test.passages, passageIndex) + 
                                  passage.questions.length + 
                                  (passage.passage2Questions?.length || 0) + 
                                  questionIndex + 1;
                                return (
                                  <TableRow 
                                    key={question._id}
                                    sx={{
                                      backgroundColor: selectedQuestionId === question._id ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                      }
                                    }}
                                    onClick={() => setSelectedQuestionId(question._id)}
                                  >
                                    <TableCell>
                                      {questionNumber}. {question.question}
                                    </TableCell>
                                    <TableCell>{submission.answers[question._id]}</TableCell>
                                    <TableCell>{question.correctAnswer}</TableCell>
                                    <TableCell>{question.standardReference}</TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                          label={(() => {
                                            const override = submission.statusOverrides?.[question._id];
                                            if (override === 'correct') return 'Correct';
                                            if (override === 'incorrect') return 'Incorrect';
                                            return submission.answers[question._id] === question.correctAnswer ? 'Correct' : 'Incorrect';
                                          })()}
                                          color={(() => {
                                            const override = submission.statusOverrides?.[question._id];
                                            if (override === 'correct') return 'success';
                                            if (override === 'incorrect') return 'error';
                                            return submission.answers[question._id] === question.correctAnswer ? 'success' : 'error';
                                          })() as any}
                                          size="small"
                                        />
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          clickable
                                          icon={<SwapHorizIcon fontSize="small" />}
                                          label="Toggle"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const current = submission.statusOverrides?.[question._id];
                                            const newStatus = current === 'correct' ? 'incorrect' : 'correct';
                                            try {
                                              const res = await apiAxiosInstance.patch(`/api/tests/${testId}/submissions/${submission._id}/answers/${question._id}/status`, { status: newStatus });
                                              const { submission: updated } = res.data;
                                              setSubmissions(prev => prev.map(s => s._id === submission._id ? {
                                                ...s,
                                                statusOverrides: updated.statusOverrides,
                                                score: updated.score
                                              } : s));
                                              setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
                                            } catch (err: any) {
                                              console.error('Error updating status', err);
                                              setSnackbar({ open: true, message: err.response?.data?.message || 'Error updating status', severity: 'error' });
                                            }
                                          }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </Box>
      <QuestionOverview
        passages={test.passages}
        selectedQuestionId={selectedQuestionId}
        onQuestionSelect={setSelectedQuestionId}
      />

      {/* Class Analysis Results Dialog */}
      <Dialog
        open={classAnalysisDialogOpen}
        onClose={() => setClassAnalysisDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PsychologyIcon color="primary" />
            <Typography variant="h6">
              Class Performance Analysis - {classAnalysis?.testTitle}
            </Typography>
            <IconButton
              edge="end"
              onClick={() => setClassAnalysisDialogOpen(false)}
              sx={{ marginLeft: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {classAnalysis && (
            <Box>
              {/* Overall Performance Summary */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="h6" gutterBottom>Overall Class Performance</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <Typography variant="h4">{classAnalysis.totalStudents}</Typography>
                    <Typography variant="subtitle1">Total Students</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4">{classAnalysis.averageScore.toFixed(1)}%</Typography>
                    <Typography variant="subtitle1">Class Average</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4">{classAnalysis.questionsAnalyzed}</Typography>
                    <Typography variant="subtitle1">Questions Analyzed</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Overall Summary */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Overall Performance Summary</Typography>
                <Typography variant="body1">{classAnalysis.overallSummary}</Typography>
              </Paper>

              {/* Standards Performance */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Performance by Standard</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Standard</strong></TableCell>
                        <TableCell align="center"><strong>Questions</strong></TableCell>
                        <TableCell align="center"><strong>Accuracy</strong></TableCell>
                        <TableCell align="center"><strong>Performance</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classAnalysis.standardsAnalysis.map((standard, index) => (
                        <TableRow key={index}>
                          <TableCell>{standard.standard}</TableCell>
                          <TableCell align="center">{standard.questionsCount}</TableCell>
                          <TableCell align="center">{standard.averageAccuracy.toFixed(1)}%</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={standard.performance}
                              color={
                                standard.performance === 'Strong' ? 'success' :
                                standard.performance === 'Adequate' ? 'warning' : 'error'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Question-by-Question Analysis */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Question-by-Question Analysis</Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width="8%"><strong>Q#</strong></TableCell>
                        <TableCell width="25%"><strong>Question</strong></TableCell>
                        <TableCell width="15%"><strong>Standard</strong></TableCell>
                        <TableCell width="10%"><strong>Accuracy</strong></TableCell>
                        <TableCell width="15%"><strong>Most Common Wrong Answer</strong></TableCell>
                        <TableCell width="12%"><strong>Difficulty</strong></TableCell>
                        <TableCell width="15%"><strong>Key Misconceptions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classAnalysis.questionAnalysis.map((question, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {question.question}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{question.standardReference}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">{question.accuracy.toFixed(1)}%</Typography>
                              <Chip
                                label={
                                  question.accuracy >= 80 ? 'Easy' :
                                  question.accuracy >= 60 ? 'Medium' : 'Hard'
                                }
                                color={
                                  question.accuracy >= 80 ? 'success' :
                                  question.accuracy >= 60 ? 'warning' : 'error'
                                }
                                size="small"
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {question.commonWrongAnswers.length > 0 ? (
                              <Box>
                                <Typography variant="body2">
                                  <strong>{question.commonWrongAnswers[0].answer}</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({question.commonWrongAnswers[0].count} students, {question.commonWrongAnswers[0].percentage.toFixed(1)}%)
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                question.accuracy >= 80 ? 'Easy' :
                                question.accuracy >= 60 ? 'Medium' : 'Hard'
                              }
                              color={
                                question.accuracy >= 80 ? 'success' :
                                question.accuracy >= 60 ? 'warning' : 'error'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {question.misconceptions.length > 0 ? (
                              <Box>
                                {question.misconceptions.slice(0, 2).map((misconception, idx) => (
                                  <Typography key={idx} variant="caption" display="block">
                                    • {misconception}
                                  </Typography>
                                ))}
                                {question.misconceptions.length > 2 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{question.misconceptions.length - 2} more...
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">None identified</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Strengths and Areas for Improvement */}
              {(classAnalysis.strengthAreas.length > 0 || classAnalysis.strugglingAreas.length > 0) && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {classAnalysis.strengthAreas.length > 0 && (
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                        <Typography variant="h6" gutterBottom>Areas of Strength</Typography>
                        <List dense>
                          {classAnalysis.strengthAreas.map((strength, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={`• ${strength}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  )}
                  {classAnalysis.strugglingAreas.length > 0 && (
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        <Typography variant="h6" gutterBottom>Areas Needing Support</Typography>
                        <List dense>
                          {classAnalysis.strugglingAreas.map((area, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={`• ${area}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Recommendations */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Instructional Recommendations</Typography>
                <List>
                  {classAnalysis.recommendations.map((recommendation, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={recommendation}
                        primaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {hasExistingClassAnalysis && (
            <Button 
              onClick={() => {
                setClassAnalysisDialogOpen(false);
                analyzeClassPerformance(true);
              }}
              color="secondary"
              variant="outlined"
              startIcon={<PsychologyIcon />}
            >
              Re-analyze Class
            </Button>
          )}
          <Button onClick={() => setClassAnalysisDialogOpen(false)} variant="contained">
            Close Analysis
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Analysis Results Dialog */}
      <Dialog
        open={analysisDialogOpen}
        onClose={() => setAnalysisDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PsychologyIcon color="primary" />
            <Typography variant="h6">
              AI Misconception Analysis - {currentAnalysis?.studentName}
            </Typography>
            <IconButton
              edge="end"
              onClick={() => setAnalysisDialogOpen(false)}
              sx={{ marginLeft: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {currentAnalysis && (
            <Box>
              {/* Performance Summary */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>Performance Summary</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography><strong>Total Questions:</strong> {currentAnalysis.totalQuestions}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><strong>Incorrect Answers:</strong> {currentAnalysis.incorrectAnswers}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography><strong>Overall Score:</strong> {currentAnalysis.overallScore.toFixed(1)}%</Typography>
                  </Grid>
                </Grid>
                <Typography sx={{ mt: 2 }}>{currentAnalysis.summary}</Typography>
              </Paper>

              {/* Misconceptions Analysis */}
              {currentAnalysis.misconceptions.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Specific Misconceptions</Typography>
                  {currentAnalysis.misconceptions.map((misconception, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'error.light' }}>
                      <Chip 
                        label={misconception.misconceptionType} 
                        color="error" 
                        size="small" 
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="subtitle1" gutterBottom>
                        <strong>Question:</strong> {misconception.question}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography>
                            <strong>Student Answer:</strong> {misconception.studentAnswer}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>
                            <strong>Correct Answer:</strong> {misconception.correctAnswer}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Typography paragraph>
                        <strong>Analysis:</strong> {misconception.explanation}
                      </Typography>
                      <Typography paragraph>
                        <strong>Cognitive Error:</strong> {misconception.cognitiveError}
                      </Typography>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <strong>Intervention Suggestion:</strong> {misconception.interventionSuggestion}
                      </Alert>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography><strong>Excellent Performance!</strong> No misconceptions identified - all answers were correct.</Typography>
                </Alert>
              )}

              {/* Recommendations */}
              <Box>
                <Typography variant="h6" gutterBottom>Recommendations</Typography>
                <List>
                  {currentAnalysis.recommendations.map((recommendation, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={recommendation}
                        primaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {currentAnalysis && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PsychologyIcon />}
              onClick={() => {
                // Find the submission that matches the current analysis
                const submission = submissions.find(s => 
                  `${s.studentInfo.firstName} ${s.studentInfo.lastName}` === currentAnalysis.studentName
                );
                if (submission) {
                  analyzeWithAI(submission, true); // true indicates re-analysis
                }
              }}
              disabled={currentAnalysis && analyzingSubmissions.has(
                submissions.find(s => 
                  `${s.studentInfo.firstName} ${s.studentInfo.lastName}` === currentAnalysis.studentName
                )?._id || ''
              )}
            >
              {currentAnalysis && analyzingSubmissions.has(
                submissions.find(s => 
                  `${s.studentInfo.firstName} ${s.studentInfo.lastName}` === currentAnalysis.studentName
                )?._id || ''
              ) ? 'Re-analyzing...' : 'Re-analyze'}
            </Button>
          )}
          <Button onClick={() => setAnalysisDialogOpen(false)} variant="contained">
            Close Analysis
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GradeSubmissions; 